import { Link } from "@tanstack/react-router";
import type { StateInfo } from "@/lib/states";
import { countyNounForCount } from "@/lib/county-label";
import {
  CDC_STATUSES,
  CDC_STATUS_LABEL,
  CDC_STATUS_SOURCE,
  CDC_STATUS_VAR,
  type CdcCountyStatus,
} from "@/lib/county-status-text";
import { nationalTickTally, stateTickTally } from "@/lib/state-tick-tally";
import { cn } from "@/lib/utils";

/**
 * Where the Lone Star tick lives, counted the way CDC counts it: for each state, how many
 * of its counties are established, how many reported, and how many have no records. One
 * row per state, ranked by the share of counties where the tick is established, so the
 * answer to "is it in my state, and how much of it?" is a glance down the list.
 *
 * Built as a list rather than a table for the same reason as the season chart: a screen
 * reader hears "Arkansas: 73 of 75 counties established, none reported" in one go.
 */
/** Established first, so each bar starts with the share where the tick lives. */
const LEADING_FIRST = [...CDC_STATUSES].reverse();

export function RangeTable({ states }: { states: StateInfo[] }) {
  const rows = states
    .map((s) => ({ state: s, tally: stateTickTally(s.abbr) }))
    .filter((r) => r.tally.total > 0 && r.tally.established + r.tally.reported > 0)
    .sort(
      (a, b) =>
        b.tally.established / b.tally.total - a.tally.established / a.tally.total ||
        b.tally.reported / b.tally.total - a.tally.reported / a.tally.total ||
        a.state.name.localeCompare(b.state.name),
    );
  const none = states
    .filter((s) => {
      const t = stateTickTally(s.abbr);
      return t.established + t.reported === 0;
    })
    .map((s) => s.name)
    .sort();
  const n = nationalTickTally();
  const grid =
    "grid grid-cols-[7rem_minmax(0,1fr)_5.75rem] items-center gap-x-3 sm:grid-cols-[9rem_minmax(0,1fr)_6rem]";

  return (
    <div>
      <p className="measure text-[0.9375rem] leading-relaxed text-foreground">
        CDC lists Lone Star ticks as established in{" "}
        <span className="font-semibold">{n.established.toLocaleString("en-US")} counties</span>{" "}
        across {n.states} states{n.dc ? " and the District of Columbia" : ""}, and has reports from{" "}
        {n.reported.toLocaleString("en-US")} more.
      </p>

      <div aria-hidden className={cn(grid, "mt-5 border-b border-rule pb-1.5")}>
        <span className="label-caps text-muted-foreground">State</span>
        <span className="label-caps truncate text-muted-foreground">
          <span className="sm:hidden">Counties</span>
          <span className="hidden sm:inline">Share of counties</span>
        </span>
        <span className="label-caps text-right text-muted-foreground">Established</span>
      </div>

      <ol>
        {rows.map(({ state: s, tally: t }) => {
          const nouns = countyNounForCount(s.abbr);
          const parts: Record<CdcCountyStatus, number> = {
            established: t.established,
            reported: t.reported,
            "no-records": t.total - t.established - t.reported,
          };
          return (
            <li key={s.slug} className={cn(grid, "border-b border-border py-1.5")}>
              <Link
                to="/states/$state"
                params={{ state: s.slug }}
                className="link truncate text-[0.9375rem]"
              >
                {s.name}
              </Link>
              <span aria-hidden className="flex h-4 gap-px">
                {LEADING_FIRST.map((status) =>
                  parts[status] > 0 ? (
                    <span
                      key={status}
                      className="block h-full [print-color-adjust:exact]"
                      style={{
                        width: `${(parts[status] / t.total) * 100}%`,
                        backgroundColor: CDC_STATUS_VAR[status],
                      }}
                    />
                  ) : null,
                )}
              </span>
              <span className="tabular text-right text-sm text-foreground">
                <span className="sr-only">
                  {t.total <= 1
                    ? `: ${t.established ? "established" : t.reported ? "reported" : "no records"}.`
                    : `: ${t.established} of ${t.total} ${nouns} established, ${t.reported ? `${t.reported} reported` : "none reported"}.`}
                </span>
                <span aria-hidden>
                  {t.established}
                  <span className="text-muted-foreground"> / {t.total}</span>
                </span>
              </span>
            </li>
          );
        })}
      </ol>

      <ul className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-muted-foreground">
        {LEADING_FIRST.map((status) => (
          <li key={status} className="inline-flex items-center gap-1.5">
            <span
              aria-hidden
              className="h-3 w-3 border border-border [print-color-adjust:exact]"
              style={{ backgroundColor: CDC_STATUS_VAR[status] }}
            />
            {CDC_STATUS_LABEL[status]}
          </li>
        ))}
      </ul>

      {none.length > 0 && (
        <p className="measure mt-4 text-sm leading-relaxed text-muted-foreground">
          No county records at all in {none.join(", ")}. That means none were reported to CDC, not
          that the tick could never be there.
        </p>
      )}
      <p className="mt-2 text-xs text-muted-foreground">
        Source:{" "}
        <a
          href={CDC_STATUS_SOURCE.url}
          target="_blank"
          rel="noopener noreferrer"
          className="link print-no-url"
        >
          CDC county data, {CDC_STATUS_SOURCE.year}
          <span className="sr-only"> (opens in a new tab)</span>
        </a>
        . Each state&rsquo;s page has a map of its counties.
      </p>
    </div>
  );
}
